import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Link2, Film, Images, RefreshCw } from "lucide-react";
import { TikTokQueueCard } from "@/components/social/TikTokQueueCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tiktok`;

export const TikTokPanel = () => {
  const [acc, setAcc] = useState(null);
  const [creator, setCreator] = useState(null);
  const [items, setItems] = useState([]);
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [a, q] = await Promise.all([axios.get(`${API}/account`), axios.get(`${API}/queue`)]);
    setAcc(a.data);
    setItems(q.data.items || []);
    if (a.data.connected) axios.get(`${API}/creator-info`).then((r) => setCreator(r.data)).catch(() => setCreator(null));
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (!items.some((i) => i.status === "publishing")) return undefined;
    const t = setInterval(() => load().catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [items, load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  const connect = () => run("connect", async () => { window.location.href = (await axios.get(`${API}/connect-url`)).data.url; });

  if (!acc) return <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />;
  return (
    <div data-testid="tiktok-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
        <div className="flex items-center gap-3">
          {acc.avatar_url && <img src={acc.avatar_url} alt="" className="h-10 w-10 rounded-full" />}
          <div>
            <p data-testid="tiktok-account-name" className="text-sm font-medium text-zinc-100">
              {acc.connected ? (acc.display_name || "TikTok conectat") : "TikTok neconectat"}
            </p>
            <p className="text-xs text-zinc-500">
              {!acc.configured ? "Lipsesc cheile aplicației TikTok (Client Key / Secret)." : acc.needs_reconnect ? "Tokenul a expirat — reconectează." : "Postările ies „Doar eu” până trece auditul TikTok."}
            </p>
          </div>
        </div>
        <button onClick={connect} disabled={!acc.configured || busy === "connect"} data-testid="tiktok-connect-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-pink-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40">
          <Link2 className="h-3.5 w-3.5" />{acc.connected ? "Reconectează TikTok" : "Conectează TikTok"}
        </button>
      </div>
      {acc.connected && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="handle produs Shopify" data-testid="tiktok-handle-input"
            className="min-w-[220px] flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-100" />
          <button onClick={() => run("photo", () => axios.post(`${API}/queue/carousel`, { product_handle: handle.trim() }), "Carusel pregătit")}
            disabled={!handle.trim() || !!busy} data-testid="tiktok-add-carousel-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 disabled:opacity-40">
            {busy === "photo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Images className="h-3.5 w-3.5" />}Pregătește carusel
          </button>
          <button onClick={() => run("video", async () => { const r = await axios.post(`${API}/queue/video`); if (!r.data.ok) throw new Error(r.data.skipped); }, "Video pregătit")}
            disabled={!!busy} data-testid="tiktok-add-video-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 disabled:opacity-40">
            {busy === "video" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}Pregătește video
          </button>
          <button onClick={() => load()} data-testid="tiktok-refresh-btn" className="rounded-md border border-zinc-800 p-1.5 text-zinc-400"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {items.length === 0 ? (
        <p data-testid="tiktok-queue-empty" className="text-sm text-zinc-500">Nicio postare TikTok în coadă.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((i) => <TikTokQueueCard key={`${i.id}-${i.status}`} item={i} creator={creator} onChange={load} />)}
        </div>
      )}
    </div>
  );
};
